import { useState } from 'react'
import { Alert, App, Button, Modal, Popconfirm, Select, Space, Table, Tag, Typography, Upload } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { DeleteOutlined, PaperClipOutlined, UploadOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { usePermissions } from '@/shared/auth/usePermissions'
import { formatDateTime } from '@/shared/lib/format'
import { formatBytes } from '@/shared/lib/format.units'
import { actorLabel } from '@/features/identity/actorName'
import { useActorDirectory } from '@/features/identity/useActorDirectory'
import {
  EXPENSE_ATTACHMENT_ACCEPT,
  EXPENSE_ATTACHMENT_KINDS,
  EXPENSE_ATTACHMENT_MAX_BYTES,
  deleteExpenseAttachment,
  expenseAttachmentUrl,
  fetchExpenseAttachments,
  uploadExpenseAttachment,
  type ExpenseAttachment,
  type ExpenseAttachmentKind,
} from './expenseAttachmentApi'
import type { JournalEntry } from './financeApi'

interface ExpenseAttachmentsModalProps {
  entry: JournalEntry | null
  onClose: () => void
}

export function ExpenseAttachmentsModal({ entry, onClose }: ExpenseAttachmentsModalProps) {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const { message } = App.useApp()
  const actors = useActorDirectory()
  const queryClient = useQueryClient()
  const [kind, setKind] = useState<ExpenseAttachmentKind | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [opening, setOpening] = useState<string | null>(null)

  const entryId = entry?.id ?? ''
  const canRead = can('finance.period.view')
  const canWrite = can('finance.expense.write')
  const listKey = queryKeys.finance.expenseAttachments(entryId)

  const attachments = useQuery({
    queryKey: listKey,
    queryFn: ({ signal }) => fetchExpenseAttachments(entryId, signal),
    enabled: entry !== null && canRead,
  })

  const upload = useMutation({
    mutationFn: (input: { kind: ExpenseAttachmentKind; file: File }) =>
      uploadExpenseAttachment(entryId, input.kind, input.file),
    onSuccess: () => {
      setKind(null)
      void message.success(t('common.toastCreated'))
      void queryClient.invalidateQueries({ queryKey: listKey })
    },
    onError: (error) => setProblem(translateError(error, t)),
  })

  const remove = useMutation({
    mutationFn: (attachmentId: string) => deleteExpenseAttachment(entryId, attachmentId),
    onSuccess: () => {
      void message.success(t('common.toastDeleted'))
      void queryClient.invalidateQueries({ queryKey: listKey })
    },
    onError: (error) => setProblem(translateError(error, t)),
  })

  const pick = (file: File) => {
    setProblem(null)
    if (kind === null) return
    if (file.size > EXPENSE_ATTACHMENT_MAX_BYTES) {
      setProblem(t('finance.attachment.tooLarge'))
      return
    }
    upload.mutate({ kind, file })
  }

  const open = async (attachment: ExpenseAttachment) => {
    setProblem(null)
    setOpening(attachment.id)
    try {
      window.open(await expenseAttachmentUrl(entryId, attachment.id), '_blank', 'noopener')
    } catch (error) {
      setProblem(translateError(error, t))
    } finally {
      setOpening(null)
    }
  }

  const close = () => {
    setKind(null)
    setProblem(null)
    upload.reset()
    remove.reset()
    onClose()
  }

  const columns: ColumnsType<ExpenseAttachment> = [
    {
      key: 'kind',
      title: t('finance.attachment.kindLabel'),
      width: 170,
      render: (_, row) => <Tag>{t(`finance.attachment.kind.${row.kind}`)}</Tag>,
    },
    {
      key: 'file',
      title: t('finance.attachment.file'),
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Button
            type="link"
            size="small"
            icon={<PaperClipOutlined />}
            loading={opening === row.id}
            onClick={() => void open(row)}
            style={{ paddingInline: 0 }}
          >
            {row.originalName ?? t('finance.attachment.unnamed')}
          </Button>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {formatBytes(row.sizeBytes, 1)}
          </Typography.Text>
        </Space>
      ),
    },
    {
      key: 'createdBy',
      title: t('finance.attachment.addedBy'),
      width: 200,
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <span>{actorLabel(row.createdBy, actors, t('common.dash'))}</span>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {formatDateTime(row.createdAt)}
          </Typography.Text>
        </Space>
      ),
    },
    ...(canWrite
      ? [
          {
            key: 'actions',
            title: t('common.actions'),
            width: 90,
            render: (_: unknown, row: ExpenseAttachment) => (
              <Popconfirm
                title={t('finance.attachment.deleteConfirm')}
                description={t('finance.attachment.deleteHint')}
                okText={t('finance.attachment.delete')}
                okButtonProps={{ danger: true }}
                cancelText={t('common.cancel')}
                onConfirm={() => remove.mutate(row.id)}
              >
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  aria-label={t('finance.attachment.delete')}
                  loading={remove.isPending && remove.variables === row.id}
                />
              </Popconfirm>
            ),
          },
        ]
      : []),
  ]

  return (
    <Modal
      open={entry !== null}
      title={t('finance.attachment.title')}
      onCancel={close}
      footer={<Button onClick={close}>{t('common.close')}</Button>}
      destroyOnHidden
      width={760}
    >
      {entry ? (
        <Typography.Paragraph type="secondary">
          {t('finance.attachment.subtitle', { date: entry.entryDate, period: entry.periodCode })}
          {entry.memo ? ` · ${entry.memo}` : ''}
        </Typography.Paragraph>
      ) : null}

      {problem ? (
        <Alert
          type="error"
          showIcon
          closable
          onClose={() => setProblem(null)}
          style={{ marginBottom: 16 }}
          message={problem}
        />
      ) : null}

      {canWrite ? (
        <Space wrap style={{ marginBottom: 16 }}>
          <Select<ExpenseAttachmentKind>
            value={kind}
            onChange={setKind}
            placeholder={t('finance.attachment.kindPlaceholder')}
            style={{ minWidth: 220 }}
            options={EXPENSE_ATTACHMENT_KINDS.map((value) => ({
              value,
              label: t(`finance.attachment.kind.${value}`),
            }))}
          />
          <Upload
            accept={EXPENSE_ATTACHMENT_ACCEPT}
            showUploadList={false}
            disabled={kind === null || upload.isPending}
            beforeUpload={(file) => {
              pick(file)
              return false
            }}
          >
            <Button icon={<UploadOutlined />} loading={upload.isPending} disabled={kind === null}>
              {t('finance.attachment.upload')}
            </Button>
          </Upload>
          <Typography.Text type="secondary">{t('finance.attachment.limits')}</Typography.Text>
        </Space>
      ) : null}

      {canRead ? (
        <>
          {attachments.isError ? (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message={translateError(attachments.error, t)}
            />
          ) : null}
          <Table<ExpenseAttachment>
            columns={columns}
            dataSource={attachments.data ?? []}
            rowKey={(row) => row.id}
            loading={attachments.isPending}
            size="small"
            pagination={false}
            locale={{
              emptyText: attachments.isError ? t('common.error') : t('finance.attachment.empty'),
            }}
          />
        </>
      ) : (
        <Alert type="info" showIcon message={t('finance.attachment.noRead')} />
      )}
    </Modal>
  )
}
